<template>
  <div class="jd-root">

    <!-- ── Nav ─────────────────────────────────────────────────────── -->
    <nav class="jd-nav">
      <div class="nav-inner">
        <div class="nav-brand">
          <img v-if="logoUrl" :src="logoUrl" class="nav-logo" alt="Company logo" />
          <img v-else :src="projectLogo" class="nav-logo" alt="Mapiibs" />
          <span class="nav-name">{{ main_company.company_name || 'Careers' }}</span>
        </div>
        <router-link :to="{ name: 'JobListings', params: { companyCode } }" class="nav-back">
          <svg viewBox="0 0 16 16" fill="none" width="14" height="14"><path d="M10 4L6 8l4 4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
          All Jobs
        </router-link>
      </div>
    </nav>

    <!-- Skeleton -->
    <div v-if="loading" class="jd-skeleton">
      <div class="sk-hero"></div>
      <div class="jd-container" style="margin-top:32px">
        <div class="sk-block sk-block--lg"></div>
        <div class="sk-block" style="width:60%"></div>
        <div class="sk-block sk-block--para" v-for="n in 4" :key="n"></div>
      </div>
    </div>

    <!-- 404 -->
    <div v-else-if="!posting" class="not-found">
      <div class="not-found-inner">
        <svg viewBox="0 0 64 64" fill="none" width="64" height="64"><rect x="8" y="12" width="48" height="40" rx="6" stroke="#dde5f0" stroke-width="2.5"/><path d="M24 32h16M32 24v16" stroke="#dde5f0" stroke-width="2.5" stroke-linecap="round"/></svg>
        <h2 class="not-found-title">Job Not Found</h2>
        <p class="not-found-sub">This position may have been filled or removed.</p>
        <router-link :to="{ name: 'JobListings', params: { companyCode } }" class="btn-primary">View All Openings</router-link>
      </div>
    </div>

    <template v-else>

      <!-- ── Hero band ──────────────────────────────────────────────── -->
      <section class="jd-hero">
        <div class="jd-container">
          <div class="jd-hero-chips">
            <span v-if="posting.requisition?.employment_type" class="chip chip--type" :class="typeClass(posting.requisition.employment_type)">
              {{ typeLabel(posting.requisition.employment_type) }}
            </span>
            <span v-if="deptName" class="chip chip--dept">
              <svg viewBox="0 0 14 14" fill="none" width="12" height="12"><rect x="1" y="3.5" width="12" height="9" rx="1.5" stroke="currentColor" stroke-width="1.3"/><path d="M4.5 3.5V2.5A1 1 0 015.5 1.5h3a1 1 0 011 1v1" stroke="currentColor" stroke-width="1.3"/></svg>
              {{ deptName }}
            </span>
            <span v-if="locationName" class="chip chip--loc">
              <svg viewBox="0 0 14 14" fill="none" width="12" height="12"><path d="M7 1.5C5.07 1.5 3.5 3.07 3.5 5c0 2.625 3.5 7 3.5 7s3.5-4.375 3.5-7C10.5 3.07 8.93 1.5 7 1.5z" stroke="currentColor" stroke-width="1.3"/><circle cx="7" cy="5" r="1.5" stroke="currentColor" stroke-width="1.3"/></svg>
              {{ locationName }}
            </span>
            <span v-if="posting.application_deadline" class="chip chip--deadline" :class="{ 'chip--soon': isSoon(posting.application_deadline) }">
              <svg viewBox="0 0 14 14" fill="none" width="12" height="12"><circle cx="7" cy="7" r="5.5" stroke="currentColor" stroke-width="1.3"/><path d="M7 4.5V7l1.5 1.5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>
              Closes {{ fmtDate(posting.application_deadline) }}
              <span v-if="isClosed(posting.application_deadline)" class="chip-pill chip-pill--red">Closed</span>
            </span>
          </div>

          <h1 class="jd-title">{{ posting.title }}</h1>
          <p class="jd-ref">Ref: {{ posting.reference }}</p>

          <!-- Share bar -->
          <div class="share-bar">
            <span class="share-label">Share:</span>
            <a :href="linkedinUrl" target="_blank" class="share-btn share-btn--li" title="Share on LinkedIn">
              <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16"><path d="M20.447 20.452h-3.554v-5.569c0-1.327-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
            </a>
            <a :href="twitterUrl" target="_blank" class="share-btn share-btn--tw" title="Share on X / Twitter">
              <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
            </a>
            <a :href="facebookUrl" target="_blank" class="share-btn share-btn--fb" title="Share on Facebook">
              <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
            </a>
            <a :href="whatsappUrl" target="_blank" class="share-btn share-btn--wa" title="Share on WhatsApp">
              <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
            </a>
            <button class="share-btn share-btn--copy" @click="copyLink" :title="copied ? 'Copied!' : 'Copy link'">
              <svg v-if="!copied" viewBox="0 0 16 16" fill="none" width="14" height="14"><rect x="5.5" y="1.5" width="9" height="11" rx="1.5" stroke="currentColor" stroke-width="1.4"/><rect x="1.5" y="4.5" width="9" height="11" rx="1.5" stroke="currentColor" stroke-width="1.4"/></svg>
              <svg v-else viewBox="0 0 16 16" fill="none" width="14" height="14"><path d="M3 8l3 3 7-7" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
              {{ copied ? 'Copied!' : 'Copy link' }}
            </button>
          </div>
        </div>
      </section>

      <!-- ── Body ───────────────────────────────────────────────────── -->
      <div class="jd-container jd-body">

        <!-- Left: content -->
        <div class="jd-content">

          <!-- Description -->
          <section class="jd-section" v-if="posting.description">
            <h2 class="jd-section-title">About the Role</h2>
            <div class="jd-prose" v-html="posting.description"></div>
          </section>

          <!-- Requirements -->
          <section class="jd-section" v-if="posting.requirements">
            <h2 class="jd-section-title">Requirements</h2>
            <div class="jd-prose" v-html="posting.requirements"></div>
          </section>

          <!-- Job facts from requisition -->
          <section class="jd-section" v-if="hasReqDetails">
            <h2 class="jd-section-title">Position Details</h2>
            <div class="facts-grid">
              <div v-if="posting.requisition?.employment_type" class="fact-item">
                <span class="fact-label">Employment Type</span>
                <span class="fact-value">{{ typeLabel(posting.requisition.employment_type) }}</span>
              </div>
              <div v-if="posting.requisition?.work_arrangement" class="fact-item">
                <span class="fact-label">Work Arrangement</span>
                <span class="fact-value">{{ ucFirst(posting.requisition.work_arrangement) }}</span>
              </div>
              <div v-if="posting.requisition?.position_count" class="fact-item">
                <span class="fact-label">Openings</span>
                <span class="fact-value">{{ posting.requisition.position_count }}</span>
              </div>
              <div v-if="posting.requisition?.min_academic_qualification" class="fact-item">
                <span class="fact-label">Min. Qualification</span>
                <span class="fact-value">{{ posting.requisition.min_academic_qualification }}</span>
              </div>
              <div v-if="posting.requisition?.min_years_experience" class="fact-item">
                <span class="fact-label">Experience</span>
                <span class="fact-value">{{ posting.requisition.min_years_experience }}+ years</span>
              </div>
              <div v-if="posting.requisition?.nysc_requirement && posting.requisition.nysc_requirement !== 'not_applicable'" class="fact-item">
                <span class="fact-label">NYSC</span>
                <span class="fact-value">{{ ucFirst(posting.requisition.nysc_requirement) }}</span>
              </div>
            </div>
          </section>

          <!-- Mobile apply button -->
          <button class="apply-btn apply-btn--mobile" @click="openDrawer" :disabled="isClosed(posting.application_deadline)">
            {{ isClosed(posting.application_deadline) ? 'Applications Closed' : 'Apply for this Role' }}
          </button>
        </div>

        <!-- Right: sidebar -->
        <aside class="jd-sidebar">
          <div class="sidebar-card">
            <div class="sidebar-company">
              <img v-if="logoUrl" :src="logoUrl" class="sidebar-logo" alt="Company logo" />
              <img v-else :src="projectLogo" class="sidebar-logo" alt="Mapiibs" />
              <div>
                <p class="sidebar-company-name">{{ company.name }}</p>
                <p class="sidebar-company-email">{{ company.email }}</p>
              </div>
            </div>
            <hr class="sidebar-hr" />
            <div class="sidebar-facts">
              <div class="sidebar-fact" v-if="deptName"><span class="sidebar-fact-label">Department</span><span class="sidebar-fact-val">{{ deptName }}</span></div>
              <div class="sidebar-fact" v-if="locationName"><span class="sidebar-fact-label">Location</span><span class="sidebar-fact-val">{{ locationName }}</span></div>
              <div class="sidebar-fact" v-if="posting.requisition?.employment_type"><span class="sidebar-fact-label">Type</span><span class="sidebar-fact-val">{{ typeLabel(posting.requisition.employment_type) }}</span></div>
              <div class="sidebar-fact" v-if="posting.application_deadline"><span class="sidebar-fact-label">Deadline</span><span class="sidebar-fact-val" :class="{ 'text-red': isSoon(posting.application_deadline) }">{{ fmtDate(posting.application_deadline) }}</span></div>
            </div>
            <button class="apply-btn apply-btn--full" @click="openDrawer" :disabled="isClosed(posting.application_deadline)">
              <svg viewBox="0 0 16 16" fill="none" width="15" height="15"><path d="M2 8h12M8 3l5 5-5 5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
              {{ isClosed(posting.application_deadline) ? 'Applications Closed' : 'Apply for this Role' }}
            </button>
            <p class="sidebar-hint">Takes about 3–5 minutes</p>
          </div>
        </aside>

      </div>

    </template>

    <!-- ═════════════════════  APPLICATION DRAWER  ═════════════════════ -->
    <transition name="drawer">
      <div class="drawer-overlay" v-if="drawerOpen" @click.self="closeDrawer">
        <aside class="drawer">

          <!-- Drawer head -->
          <div class="drawer-head">
            <div>
              <p class="drawer-title">Apply for this Role</p>
              <p class="drawer-sub">{{ posting?.title }}</p>
            </div>
            <button class="drawer-close" @click="closeDrawer">
              <svg viewBox="0 0 16 16" fill="none" width="16" height="16"><path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>
            </button>
          </div>

          <!-- Success state -->
          <div v-if="submitted" class="drawer-success">
            <div class="success-circle">
              <svg viewBox="0 0 48 48" fill="none" width="56" height="56"><circle cx="24" cy="24" r="22" fill="#dcfce7" stroke="#16a34a" stroke-width="2"/><path d="M14 24l7 7 13-13" stroke="#16a34a" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </div>
            <h3 class="success-title">Application Submitted!</h3>
            <p class="success-msg">Thank you, {{ form.first_name }}! We've received your application and will be in touch shortly.</p>
            <button class="apply-btn apply-btn--full" style="margin-top:20px" @click="closeDrawer">Done</button>
          </div>

          <!-- Form -->
          <form v-else class="drawer-form" @submit.prevent="submitApplication" novalidate>

            <!-- Personal info -->
            <div class="form-section-label">Personal Information</div>
            <div class="field-row">
              <div class="field">
                <label class="field-label">First Name <span class="req">*</span></label>
                <input type="text" class="field-input" v-model.trim="form.first_name" :class="{ 'field-input--err': errors.first_name }" autocomplete="given-name" />
                <span v-if="errors.first_name" class="field-err">{{ errors.first_name }}</span>
              </div>
              <div class="field">
                <label class="field-label">Last Name <span class="req">*</span></label>
                <input type="text" class="field-input" v-model.trim="form.last_name" :class="{ 'field-input--err': errors.last_name }" autocomplete="family-name" />
                <span v-if="errors.last_name" class="field-err">{{ errors.last_name }}</span>
              </div>
            </div>
            <div class="field-row">
              <div class="field">
                <label class="field-label">Email Address <span class="req">*</span></label>
                <input type="email" class="field-input" v-model.trim="form.email" :class="{ 'field-input--err': errors.email }" autocomplete="email" />
                <span v-if="errors.email" class="field-err">{{ errors.email }}</span>
              </div>
              <div class="field">
                <label class="field-label">Phone Number <span class="req">*</span></label>
                <input type="tel" class="field-input" v-model.trim="form.phone" :class="{ 'field-input--err': errors.phone }" autocomplete="tel" />
                <span v-if="errors.phone" class="field-err">{{ errors.phone }}</span>
              </div>
            </div>
            <div class="field">
              <label class="field-label">Gender <span class="req">*</span></label>
              <div class="gender-group" :class="{ 'gender-group--err': errors.gender }">
                <label v-for="g in genderOptions" :key="g.value" class="gender-opt" :class="{ 'gender-opt--active': form.gender === g.value }">
                  <input type="radio" :value="g.value" v-model="form.gender" class="sr-only" />
                  {{ g.label }}
                </label>
              </div>
              <span v-if="errors.gender" class="field-err">{{ errors.gender }}</span>
            </div>

            <!-- Resume -->
            <div class="form-section-label" style="margin-top:18px">Resume / CV</div>
            <div class="field">
              <label class="field-label">Upload Resume <span class="req">*</span></label>
              <div class="upload-zone" :class="{ 'upload-zone--active': dragging, 'upload-zone--filled': !!form.resume, 'upload-zone--err': errors.resume }" @dragover.prevent="dragging=true" @dragleave="dragging=false" @drop.prevent="onDrop">
                <input type="file" ref="resumeInput" accept=".pdf,.doc,.docx" class="upload-input" @change="onFileChange" />
                <div v-if="!form.resume" class="upload-placeholder" @click="$refs.resumeInput.click()">
                  <svg viewBox="0 0 32 32" fill="none" width="32" height="32"><rect x="4" y="2" width="18" height="24" rx="2" stroke="#93c5fd" stroke-width="1.8"/><path d="M22 2v8h8" stroke="#93c5fd" stroke-width="1.8" stroke-linejoin="round"/><path d="M22 2l8 8" stroke="#93c5fd" stroke-width="1.8"/><path d="M8 18h8M8 22h5" stroke="#93c5fd" stroke-width="1.8" stroke-linecap="round"/></svg>
                  <p class="upload-text">Drag & drop or <span class="upload-link">browse</span></p>
                  <p class="upload-hint">PDF, DOC, DOCX — max 5 MB</p>
                </div>
                <div v-else class="upload-filled" @click="$refs.resumeInput.click()">
                  <svg viewBox="0 0 20 20" fill="none" width="20" height="20"><path d="M4 2h9l5 5v11a1 1 0 01-1 1H4a1 1 0 01-1-1V3a1 1 0 011-1z" fill="#dbeafe" stroke="#3b82f6" stroke-width="1.4"/><path d="M13 2v5h5" stroke="#3b82f6" stroke-width="1.4"/><path d="M7 12h6M7 15h4" stroke="#3b82f6" stroke-width="1.4" stroke-linecap="round"/></svg>
                  <div>
                    <p class="upload-filename">{{ form.resume.name }}</p>
                    <p class="upload-filesize">{{ fmtSize(form.resume.size) }}</p>
                  </div>
                  <button type="button" class="upload-remove" @click.stop="form.resume = null">
                    <svg viewBox="0 0 14 14" fill="none" width="12" height="12"><path d="M2 2l10 10M12 2L2 12" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>
                  </button>
                </div>
              </div>
              <span v-if="errors.resume" class="field-err">{{ errors.resume }}</span>
            </div>

            <!-- Cover letter -->
            <div class="form-section-label" style="margin-top:18px">Cover Letter <span class="optional-tag">Optional</span></div>
            <div class="field">
              <textarea class="field-input field-textarea" v-model="form.cover_letter" rows="4" placeholder="Tell us why you're a great fit for this role…" maxlength="5000"></textarea>
              <span class="field-count">{{ (form.cover_letter || '').length }}/5000</span>
            </div>

            <!-- Hear about us -->
            <div class="form-section-label" style="margin-top:18px">How Did You Hear About Us?</div>
            <div class="field">
              <select class="field-input" v-model="form.hear_about_us">
                <option value="">— select —</option>
                <option value="linkedin">LinkedIn</option>
                <option value="job_board">Job Board (Jobberman, MyJobMag, etc.)</option>
                <option value="referral">Friend / Employee Referral</option>
                <option value="company_website">Company Website</option>
                <option value="social_media">Social Media</option>
                <option value="recruitment_agency">Recruitment Agency</option>
                <option value="other">Other</option>
              </select>
            </div>

            <!-- Consent -->
            <div class="form-section-label" style="margin-top:18px">Consent</div>
            <label class="consent-label" :class="{ 'consent-label--err': errors.consent }">
              <input type="checkbox" v-model="form.consent" class="consent-check" />
              <span class="consent-text">
                I consent to {{ company.name || 'this company' }} collecting and processing my personal data for the purpose of this job application. I understand my data will be handled in accordance with applicable data protection laws. <span class="req">*</span>
              </span>
            </label>
            <span v-if="errors.consent" class="field-err" style="margin-top:4px;display:block">{{ errors.consent }}</span>

            <!-- API error -->
            <div v-if="apiError" class="api-error">{{ apiError }}</div>

            <!-- Actions -->
            <div class="drawer-actions">
              <button type="button" class="btn-ghost" @click="closeDrawer">Cancel</button>
              <button type="submit" class="apply-btn apply-btn--full" :disabled="submitting" style="min-width:160px">
                <svg v-if="!submitting" viewBox="0 0 16 16" fill="none" width="14" height="14"><path d="M2 8h12M8 3l5 5-5 5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
                <span class="spinner" v-if="submitting"></span>
                {{ submitting ? 'Submitting…' : 'Submit Application' }}
              </button>
            </div>

          </form>
        </aside>
      </div>
    </transition>

    <!-- Footer -->
    <footer class="careers-footer">
      <div class="footer-inner">
        <p class="footer-company">{{ main_company.company_name }}</p>
        <p class="footer-copy">© {{ year }} All rights reserved.</p>
      </div>
    </footer>

  </div>
</template>

<script>
import axios from 'axios';
import host from '../main';
import projectLogo from '../private/private-assets/images/logo.png';

export default {
  name: 'JobDetail',

  data() {
    return {
      loading:    true,
      posting:    null,
      company:    {},
      main_company:   {},
      hostName:    host.hostName,
      projectLogo: projectLogo,
      drawerOpen: false,
      dragging:   false,
      submitting: false,
      submitted:  false,
      copied:     false,
      apiError:   '',

      form: {
        first_name:    '',
        last_name:     '',
        email:         '',
        phone:         '',
        gender:        '',
        resume:        null,
        cover_letter:  '',
        hear_about_us: '',
        consent:       false,
      },
      errors: {},

      genderOptions: [
        { value: 'male',              label: 'Male'              },
        { value: 'female',            label: 'Female'            },
        { value: 'other',             label: 'Other'             },
        { value: 'prefer_not_to_say', label: 'Prefer not to say' },
      ],
    };
  },

  computed: {
    companyCode() { return this.$route?.params?.companyCode; },
    slug()        { return this.$route?.params?.slug; },
    year()        { return new Date().getFullYear(); },

    logoUrl() { return this.main_company.logo ? this.hostName + this.main_company.logo : null; },
    companyInitials() {
      const name = this.main_company.company_name || '';
      return name.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase() || '?';
    },

    deptName()    { return this.posting?.requisition?.department?.name || ''; },
    locationName(){
      return this.posting?.requisition?.company_location?.name
          || this.posting?.requisition?.location
          || '';
    },
    hasReqDetails() {
      const r = this.posting?.requisition;
      return r && (r.employment_type || r.work_arrangement || r.position_count || r.min_academic_qualification || r.min_years_experience || r.nysc_requirement);
    },

    shareUrl() { return `${window.location.origin}/careers/${this.companyCode}/${this.slug}`; },
    encodedUrl() { return encodeURIComponent(this.shareUrl); },
    encodedTitle() { return encodeURIComponent(`${this.posting?.title || 'Job'} at ${this.company.name || 'us'}`); },

    linkedinUrl()  { return `https://www.linkedin.com/sharing/share-offsite/?url=${this.encodedUrl}`; },
    twitterUrl()   { return `https://twitter.com/intent/tweet?text=${this.encodedTitle}&url=${this.encodedUrl}`; },
    facebookUrl()  { return `https://www.facebook.com/sharer/sharer.php?u=${this.encodedUrl}`; },
    whatsappUrl()  { return `https://api.whatsapp.com/send?text=${encodeURIComponent(this.posting?.title + ' — ' + this.shareUrl)}`; },
  },

  mounted() {
    this.fetchDetail();
  },

  methods: {
    async fetchDetail() {
      this.loading = true;
      try {
        const { data } = await axios.get(`/public/careers/${this.companyCode}/${this.slug}`);
        this.posting = data.data?.posting || null;
        this.company = data.data?.business || {};
        this.main_company = data.data?.company || {};
      } catch {
        this.posting = null;
      } finally {
        this.loading = false;
      }
    },

    openDrawer()  { this.drawerOpen = true; document.body.style.overflow = 'hidden'; },
    closeDrawer() { this.drawerOpen = false; document.body.style.overflow = ''; if (this.submitted) this.resetForm(); },

    resetForm() {
      this.form = { first_name: '', last_name: '', email: '', phone: '', gender: '', resume: null, cover_letter: '', hear_about_us: '', consent: false };
      this.errors = {}; this.apiError = ''; this.submitted = false;
    },

    // File handling
    onFileChange(e) {
      const file = e.target.files[0];
      if (file) this.setResume(file);
    },
    onDrop(e) {
      this.dragging = false;
      const file = e.dataTransfer.files[0];
      if (file) this.setResume(file);
    },
    setResume(file) {
      const allowed = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowed.includes(file.type)) { this.errors.resume = 'Only PDF, DOC or DOCX files are allowed'; return; }
      if (file.size > 5 * 1024 * 1024) { this.errors.resume = 'File must be smaller than 5 MB'; return; }
      this.form.resume = file;
      delete this.errors.resume;
    },

    // Validate
    validate() {
      const e = {};
      if (!this.form.first_name)  e.first_name = 'First name is required';
      if (!this.form.last_name)   e.last_name  = 'Last name is required';
      if (!this.form.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.form.email)) e.email = 'A valid email address is required';
      if (!this.form.phone)       e.phone  = 'Phone number is required';
      if (!this.form.gender)      e.gender = 'Please select a gender';
      if (!this.form.resume)      e.resume = 'Please upload your resume / CV';
      if (!this.form.consent)     e.consent = 'You must consent to data processing to submit your application';
      this.errors = e;
      return Object.keys(e).length === 0;
    },

    // Submit
    async submitApplication() {
      this.apiError = '';
      if (!this.validate()) {
        this.$nextTick(() => {
          const el = this.$el.querySelector('.field-input--err, .upload-zone--err, .consent-label--err');
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
        return;
      }

      this.submitting = true;
      const fd = new FormData();
      fd.append('first_name',    this.form.first_name);
      fd.append('last_name',     this.form.last_name);
      fd.append('email',         this.form.email);
      fd.append('phone',         this.form.phone);
      fd.append('gender',        this.form.gender);
      fd.append('resume',        this.form.resume);
      if (this.form.cover_letter)  fd.append('cover_letter',  this.form.cover_letter);
      if (this.form.hear_about_us) fd.append('hear_about_us', this.form.hear_about_us);
      fd.append('consent', '1');

      try {
        await axios.post(`/public/careers/${this.companyCode}/${this.slug}/apply`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        this.submitted = true;
        this.$el.querySelector('.drawer').scrollTop = 0;
      } catch (err) {
        this.apiError = err.response?.data?.message || 'Something went wrong. Please try again.';
      } finally {
        this.submitting = false;
      }
    },

    // Social
    async copyLink() {
      try { 
        await navigator.clipboard.writeText(this.shareUrl); 
      } catch {/* silent */}
      this.copied = true;
      setTimeout(() => { this.copied = false; }, 2200);
    },

    // Helpers
    isClosed(d)     { return d ? new Date(d) < new Date() : false; },
    isSoon(d)       { if (!d) return false; const diff = new Date(d) - new Date(); return diff > 0 && diff < 7 * 86400000; },
    fmtDate(d)      { return d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : ''; },
    fmtSize(bytes)  { return bytes < 1048576 ? `${(bytes / 1024).toFixed(1)} KB` : `${(bytes / 1048576).toFixed(1)} MB`; },
    typeLabel(t)    { return { permanent: 'Full-Time', contract: 'Contract', intern: 'Internship', adhoc: 'Ad-hoc' }[t] || t; },
    typeClass(t)    { return { permanent: 'chip--green', contract: 'chip--blue', intern: 'chip--purple', adhoc: 'chip--amber' }[t] || 'chip--gray'; },
    ucFirst(s)      { return s ? s.charAt(0).toUpperCase() + s.slice(1) : s; },
  },
};
</script>

<style scoped>
*, *::before, *::after { box-sizing: border-box; }
.sr-only { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0,0,0,0); }

.jd-root { min-height: 100vh; background: #f8fafc; font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif; }

/* Nav */
.jd-nav     { background: #fff; border-bottom: 1px solid #e8edf5; position: sticky; top: 0; z-index: 50; }
.nav-inner  { max-width: 1100px; margin: 0 auto; padding: 0 24px; height: 60px; display: flex; align-items: center; justify-content: space-between; }
.nav-brand  { display: flex; align-items: center; gap: 10px; }
.nav-logo   { height: 36px; width: auto; max-width: 160px; object-fit: contain; }
.nav-logo-initials { width: 32px; height: 32px; border-radius: 6px; background: #004AAD; color: #fff; font-size: 13px; font-weight: 800; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.nav-name   { font-size: 16px; font-weight: 800; color: #0f172a; letter-spacing: -0.3px; }
.nav-back   { display: inline-flex; align-items: center; gap: 5px; font-size: 13px; font-weight: 600; color: #004AAD; text-decoration: none; padding: 6px 12px; border-radius: 8px; background: #f0f7ff; border: 1px solid #dbeafe; transition: background .15s; }
.nav-back:hover { background: #dbeafe; }

/* Skeleton */
.jd-skeleton { }
.sk-hero     { height: 180px; background: linear-gradient(90deg, #f0f4f8 25%, #e4eaf2 50%, #f0f4f8 75%); background-size: 200% 100%; animation: shimmer 1.4s infinite; }
.sk-block    { height: 18px; background: linear-gradient(90deg, #f0f4f8 25%, #e4eaf2 50%, #f0f4f8 75%); background-size: 200% 100%; animation: shimmer 1.4s infinite; border-radius: 6px; margin-bottom: 14px; }
.sk-block--lg  { height: 36px; }
.sk-block--para{ height: 14px; width: 80%; }
@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }

/* Not found */
.not-found      { display: flex; align-items: center; justify-content: center; min-height: 50vh; }
.not-found-inner{ text-align: center; padding: 40px 20px; }
.not-found-title{ font-size: 24px; font-weight: 700; color: #0f172a; margin: 16px 0 8px; }
.not-found-sub  { font-size: 14px; color: #64748b; margin: 0 0 24px; }

/* Hero band */
.jd-hero    { background: linear-gradient(135deg, #0f172a 0%, #1e3a5f 60%, #004AAD 100%); padding: 40px 24px 44px; }
.jd-container { max-width: 1100px; margin: 0 auto; padding: 0 24px; }

/* Chips */
.jd-hero-chips { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 16px; }
.chip          { display: inline-flex; align-items: center; gap: 5px; padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: 600; }
.chip--green   { background: #dcfce7; color: #15803d; }
.chip--blue    { background: #dbeafe; color: #1d4ed8; }
.chip--purple  { background: #ede9fe; color: #7c3aed; }
.chip--amber   { background: #fef3c7; color: #b45309; }
.chip--gray    { background: #f1f5f9; color: #475569; }
.chip--type    { }
.chip--dept    { background: rgba(255,255,255,.12); color: rgba(255,255,255,.85); }
.chip--loc     { background: rgba(255,255,255,.12); color: rgba(255,255,255,.85); }
.chip--deadline{ background: rgba(255,255,255,.12); color: rgba(255,255,255,.75); }
.chip--soon    { background: rgba(245,158,11,.2); color: #fbbf24; }
.chip-pill     { padding: 1px 7px; border-radius: 20px; font-size: 10px; font-weight: 700; margin-left: 4px; }
.chip-pill--red{ background: #fca5a5; color: #7f1d1d; }

.jd-title { font-size: clamp(24px, 4vw, 40px); font-weight: 800; color: #fff; margin: 0 0 8px; line-height: 1.18; letter-spacing: -0.5px; }
.jd-ref   { font-size: 12px; color: rgba(255,255,255,.45); margin: 0 0 24px; font-family: monospace; }

/* Share bar */
.share-bar   { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.share-label { font-size: 12px; font-weight: 600; color: rgba(255,255,255,.5); text-transform: uppercase; letter-spacing: 0.06em; }
.share-btn   { display: inline-flex; align-items: center; gap: 5px; padding: 7px 12px; border-radius: 8px; font-size: 12px; font-weight: 600; cursor: pointer; border: 1.5px solid transparent; transition: opacity .15s; text-decoration: none; }
.share-btn:hover { opacity: .85; }
.share-btn--li   { background: #0077b5; color: #fff; }
.share-btn--tw   { background: #000; color: #fff; }
.share-btn--fb   { background: #1877f2; color: #fff; }
.share-btn--wa   { background: #25d366; color: #fff; }
.share-btn--copy { background: rgba(255,255,255,.1); color: #fff; border-color: rgba(255,255,255,.15); }

/* Body layout */
.jd-body    { display: grid; grid-template-columns: 1fr 320px; gap: 32px; padding-top: 36px; padding-bottom: 64px; align-items: start; }

/* Content */
.jd-content { min-width: 0; }
.jd-section { background: #fff; border: 1px solid #e8edf5; border-radius: 14px; padding: 28px 28px; margin-bottom: 20px; }
.jd-section-title { font-size: 16px; font-weight: 700; color: #0f172a; margin: 0 0 16px; padding-bottom: 12px; border-bottom: 1px solid #f0f4f8; }
.jd-prose   { font-size: 14px; color: #374151; line-height: 1.7; }
.jd-prose :deep(p)  { margin: 0 0 12px; }
.jd-prose :deep(ul) { padding-left: 20px; margin: 0 0 12px; }
.jd-prose :deep(li) { margin-bottom: 6px; }
.jd-prose :deep(h3) { font-size: 15px; font-weight: 700; margin: 16px 0 8px; }

/* Facts grid */
.facts-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; }
.fact-item  { display: flex; flex-direction: column; gap: 4px; padding: 14px 16px; background: #f8fafc; border: 1px solid #f0f4f8; border-radius: 10px; }
.fact-label { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; color: #94a3b8; }
.fact-value { font-size: 14px; font-weight: 600; color: #0f172a; }

/* Mobile apply */
.apply-btn--mobile { display: none; width: 100%; margin-top: 8px; }

/* Sidebar */
.jd-sidebar { }
.sidebar-card { background: #fff; border: 1px solid #e8edf5; border-radius: 14px; padding: 22px; position: sticky; top: 76px; }
.sidebar-company { display: flex; align-items: center; gap: 12px; margin-bottom: 16px; }
.sidebar-logo         { height: 44px; width: auto; max-width: 140px; object-fit: contain; border-radius: 10px; border: 1px solid #f0f4f8; background: #fff; padding: 4px 10px; }
.sidebar-logo-initials{ width: 48px; height: 48px; border-radius: 10px; background: #004AAD; color: #fff; font-size: 16px; font-weight: 800; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.sidebar-company-name  { font-size: 14px; font-weight: 700; color: #0f172a; margin: 0 0 2px; }
.sidebar-company-email { font-size: 12px; color: #64748b; margin: 0; }
.sidebar-hr { border: none; border-top: 1px solid #f0f4f8; margin: 0 0 16px; }
.sidebar-facts { display: flex; flex-direction: column; gap: 10px; margin-bottom: 20px; }
.sidebar-fact  { display: flex; justify-content: space-between; align-items: center; gap: 8px; }
.sidebar-fact-label { font-size: 12px; color: #94a3b8; font-weight: 600; }
.sidebar-fact-val   { font-size: 12px; font-weight: 700; color: #0f172a; text-align: right; }
.text-red { color: #f59e0b; }
.sidebar-hint { font-size: 11px; color: #94a3b8; text-align: center; margin: 8px 0 0; }

/* Apply button */
.apply-btn { display: inline-flex; align-items: center; justify-content: center; gap: 8px; background: #004AAD; color: #fff; font-size: 14px; font-weight: 700; padding: 0 22px; height: 44px; border-radius: 10px; border: none; cursor: pointer; transition: background .15s, transform .1s; }
.apply-btn:hover:not(:disabled) { background: #003b8e; transform: translateY(-1px); }
.apply-btn:disabled { background: #94a3b8; cursor: not-allowed; transform: none; }
.apply-btn--full { width: 100%; }

/* ── Drawer ── */
.drawer-overlay { position: fixed; inset: 0; background: rgba(15,23,42,.5); z-index: 200; display: flex; justify-content: flex-end; backdrop-filter: blur(4px); }
.drawer         { width: 100%; max-width: 520px; height: 100%; background: #fff; display: flex; flex-direction: column; overflow-y: auto; box-shadow: -8px 0 40px rgba(0,0,0,.12); }

.drawer-head { display: flex; align-items: flex-start; justify-content: space-between; padding: 22px 24px 18px; border-bottom: 1px solid #f0f4f8; position: sticky; top: 0; background: #fff; z-index: 1; }
.drawer-title{ font-size: 17px; font-weight: 800; color: #0f172a; margin: 0; }
.drawer-sub  { font-size: 12px; color: #64748b; margin: 3px 0 0; }
.drawer-close{ background: none; border: none; cursor: pointer; color: #94a3b8; padding: 4px; border-radius: 6px; }
.drawer-close:hover { background: #f1f5f9; color: #475569; }

/* Success */
.drawer-success { display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; padding: 60px 32px; flex: 1; }
.success-circle { margin-bottom: 20px; }
.success-title  { font-size: 22px; font-weight: 800; color: #0f172a; margin: 0 0 10px; }
.success-msg    { font-size: 14px; color: #64748b; line-height: 1.6; margin: 0; max-width: 340px; }

/* Form */
.drawer-form { padding: 22px 24px 32px; flex: 1; }
.form-section-label { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.07em; color: #94a3b8; margin-bottom: 12px; }
.optional-tag { text-transform: none; font-size: 10px; background: #f1f5f9; color: #64748b; padding: 2px 7px; border-radius: 20px; margin-left: 6px; font-weight: 600; letter-spacing: 0; }

.field-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 12px; }
.field { display: flex; flex-direction: column; gap: 5px; margin-bottom: 12px; }
.field:last-child { margin-bottom: 0; }
.field-label  { font-size: 12px; font-weight: 600; color: #374151; }
.req          { color: #ef4444; }
.field-input  { width: 100%; padding: 10px 13px; border: 1.5px solid #e2e8f0; border-radius: 9px; font-size: 13px; color: #0f172a; outline: none; transition: border-color .15s; background: #fff; }
.field-input:focus { border-color: #004AAD; box-shadow: 0 0 0 3px rgba(0,74,173,.08); }
.field-input--err   { border-color: #ef4444 !important; }
.field-textarea { resize: vertical; min-height: 96px; font-family: inherit; line-height: 1.5; }
.field-err  { font-size: 11px; color: #ef4444; margin-top: 2px; }
.field-count{ font-size: 11px; color: #94a3b8; text-align: right; margin-top: 3px; }

/* Gender */
.gender-group { display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px; }
.gender-opt   { display: flex; align-items: center; justify-content: center; padding: 9px 12px; border: 1.5px solid #e2e8f0; border-radius: 9px; font-size: 12px; font-weight: 600; color: #475569; cursor: pointer; transition: all .15s; text-align: center; }
.gender-opt:hover     { border-color: #93c5fd; background: #f0f7ff; color: #004AAD; }
.gender-opt--active   { border-color: #004AAD; background: #f0f7ff; color: #004AAD; }
.gender-group--err .gender-opt { border-color: #fca5a5; }

/* Upload */
.upload-zone { position: relative; border: 2px dashed #e2e8f0; border-radius: 12px; transition: border-color .15s, background .15s; }
.upload-zone--active { border-color: #93c5fd; background: #f0f7ff; }
.upload-zone--filled { border-style: solid; border-color: #bfdbfe; background: #f8fbff; }
.upload-zone--err    { border-color: #fca5a5; }
.upload-input { position: absolute; inset: 0; opacity: 0; cursor: pointer; z-index: 1; }
.upload-placeholder { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; padding: 28px 20px; cursor: pointer; }
.upload-text  { font-size: 13px; color: #64748b; margin: 0; }
.upload-link  { color: #004AAD; font-weight: 600; text-decoration: underline; cursor: pointer; }
.upload-hint  { font-size: 11px; color: #94a3b8; margin: 0; }
.upload-filled { display: flex; align-items: center; gap: 12px; padding: 14px 16px; cursor: pointer; position: relative; z-index: 2; }
.upload-filename { font-size: 13px; font-weight: 600; color: #0f172a; margin: 0 0 2px; }
.upload-filesize { font-size: 11px; color: #64748b; margin: 0; }
.upload-remove { background: none; border: none; cursor: pointer; color: #94a3b8; padding: 4px; border-radius: 6px; margin-left: auto; flex-shrink: 0; z-index: 3; }
.upload-remove:hover { background: #fee2e2; color: #ef4444; }

/* Consent */
.consent-label { display: flex; align-items: flex-start; gap: 10px; cursor: pointer; padding: 14px 16px; border: 1.5px solid #e2e8f0; border-radius: 10px; transition: border-color .15s; }
.consent-label:hover { border-color: #93c5fd; }
.consent-label--err { border-color: #fca5a5; background: #fff5f5; }
.consent-check { margin-top: 3px; width: 16px; height: 16px; flex-shrink: 0; accent-color: #004AAD; cursor: pointer; }
.consent-text  { font-size: 12px; color: #374151; line-height: 1.55; }

/* API error */
.api-error { background: #fff5f5; border: 1px solid #fca5a5; border-radius: 9px; padding: 12px 14px; font-size: 13px; color: #ef4444; margin-top: 12px; }

/* Actions */
.drawer-actions { display: flex; align-items: center; justify-content: flex-end; gap: 10px; margin-top: 22px; padding-top: 18px; border-top: 1px solid #f0f4f8; }
.btn-ghost      { background: none; border: 1px solid #e2e8f0; border-radius: 9px; padding: 0 18px; height: 44px; font-size: 13px; font-weight: 600; color: #475569; cursor: pointer; }
.btn-ghost:hover { background: #f8fafc; }

/* Spinner */
.spinner { width: 16px; height: 16px; border: 2px solid rgba(255,255,255,.3); border-top-color: #fff; border-radius: 50%; animation: spin .7s linear infinite; flex-shrink: 0; }
@keyframes spin { to { transform: rotate(360deg); } }

/* Drawer transition */
.drawer-enter-active, .drawer-leave-active { transition: opacity .25s ease; }
.drawer-enter-active .drawer, .drawer-leave-active .drawer { transition: transform .3s cubic-bezier(.4,0,.2,1); }
.drawer-enter-from, .drawer-leave-to { opacity: 0; }
.drawer-enter-from .drawer, .drawer-leave-to .drawer { transform: translateX(100%); }

/* Buttons */
.btn-primary { display: inline-flex; align-items: center; gap: 6px; background: #004AAD; color: #fff; font-size: 14px; font-weight: 700; padding: 0 22px; height: 44px; border-radius: 10px; border: none; cursor: pointer; text-decoration: none; }
.btn-primary:hover { background: #003b8e; }

/* Footer */
.careers-footer { background: #0f172a; padding: 32px 24px; }
.footer-inner   { max-width: 1100px; margin: 0 auto; display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
.footer-company { font-size: 14px; font-weight: 700; color: #fff; margin: 0; }
.footer-copy    { font-size: 12px; color: #475569; margin: 0; }

@media (max-width: 860px) {
  .jd-body { grid-template-columns: 1fr; }
  .jd-sidebar { display: none; }
  .apply-btn--mobile { display: flex; }
}
@media (max-width: 520px) {
  .field-row  { grid-template-columns: 1fr; }
  .gender-group { grid-template-columns: 1fr 1fr; }
  .drawer     { max-width: 100%; }
}
</style>
